// ─────────────────────────────────────────────────────────────
// NexiFloor2 — 넥시 창고 2층 배치도 (v7.342 — 1층 양식으로 재작성)
//
// 데이터는 NexiFloor2Data.js (구글시트 [2층 찾기] 기계 추출). 이 파일은 그리기만 한다.
// 1층 그린1(계단)으로 올라오는 층이다.
//
// ── v7.342 에서 바꾼 것: 1층(NexiMap.jsx) 과 같은 양식으로 맞췄다 ──
//   ① 칸 크기를 1층 랙과 동일하게 (37 × 21). 그전엔 27.5 × 15 라 숫자가 작았다
//   ② 칸마다 흰 배경 + 둥근 모서리 + 테두리 + 사이 여백 (1층 랙 칸과 같은 처리)
//   ③ 벽을 "검정으로 꽉 채운 칸"에서 "칸 가운데를 지나는 굵은 선"으로 바꿨다.
//      ⚠ 칸을 통째로 칠하면 가로벽은 21px, 세로벽은 37px 로 두께가 달라 보인다.
//        칸 단위가 아니라 8px 바를 가운데 놓고, 이웃한 벽 방향으로만 늘린다.
//        교차점에서는 가로·세로 바가 겹쳐 자연스럽게 십자가 된다
//   ④ 구역(방·승강기)을 칸 단위 색칠에서 하나의 둥근 패널로 병합
//   ⑤ 승/강/기, 회/의/실 처럼 한 글자씩 흩어진 라벨을 한 덩이로 합쳐 표시
//
// ⚠ 칸이 2,809개(53×53)다. 리렌더마다 전부 다시 만들면 검색 입력이 버벅인다.
//   → 배경(벽·선반·구역)은 useMemo 로 한 번만 만들고 하이라이트만 위에 덧그린다.
// ─────────────────────────────────────────────────────────────
import React, { useMemo } from 'react';
import {
  F2_ROWS, F2_COLS, F2_COL_UNITS, F2_TEXTS, F2_MERGES,
  f2FillGrid, isF2Code,
} from './NexiFloor2Data';

// ─── 치수 ── 1층 랙 칸(37 × 21)에 맞춘다 ──────────────────────
const PX_PER_UNIT = 6.3;      // 5.86 문자단위 × 6.3 ≈ 36.9px
const ROW_H = 21;
const WALL_T = 8;             // 벽 두께
const PAD = 1.1;              // 칸 사이 여백 (한쪽)

// 채우기 색인
// 1(#F3F3F3) 과 2(#EFEFEF) 는 일부러 뺐다 — 시트에 칠해 둔 음영일 뿐 창고 구조가 아니다.
//   394칸 중 307칸이 빈 칸이라 그대로 옮기면 뜻 없는 회색 덩어리만 크게 남는다 (v7.342 까지는 그렸다).
//   구조는 벽 선으로 충분하다.
const F_NONE = 0, F_WALL = 3, F_ROOM = 4, F_LIFT = 5, F_CREAM = 6, F_LABEL = 7;

const THEME = {
  light: {
    wall: '#1f2937',
    cell: '#ffffff', cellLine: '#374151', cellText: '#111827',
    head: '#f1f5f9', headLine: '#475569',
    door: '#0f766e', doorArc: 'rgba(15,118,110,0.5)',
    room: '#fce5cd', roomLine: '#e0a878', roomText: '#8a4a1c',
    lift: '#d9ead3', liftLine: '#7fae7f', liftText: '#14532d',
    cream: '#fff2cc', creamLine: '#d9b95c',
    label: '#fef08a', labelLine: '#ca8a04', labelText: '#713f12',
    faint: '#6b7280',
  },
  dark: {
    wall: '#cbd5e1',
    cell: '#111c2e', cellLine: '#64748b', cellText: '#e2e8f0',
    head: '#1e293b', headLine: '#94a3b8',
    door: '#5eead4', doorArc: 'rgba(94,234,212,0.45)',
    room: 'rgba(252,229,205,0.16)', roomLine: 'rgba(224,168,120,0.6)', roomText: '#fbd9bd',
    lift: 'rgba(217,234,211,0.16)', liftLine: 'rgba(127,174,127,0.6)', liftText: '#bbf7d0',
    cream: 'rgba(255,242,204,0.14)', creamLine: 'rgba(217,185,92,0.5)',
    label: 'rgba(250,204,21,0.3)', labelLine: '#ca8a04', labelText: '#fde68a',
    faint: '#94a3b8',
  },
};

// ─── 좌표 ────────────────────────────────────────────────────
const COL_X = (() => {
  const xs = [0, 0];
  for (let c = 1; c <= F2_COLS; c++) xs[c + 1] = xs[c] + F2_COL_UNITS(c) * PX_PER_UNIT;
  return xs;
})();
const COL_W = (c) => F2_COL_UNITS(c) * PX_PER_UNIT;

// 실제로 쓰이는 범위만 남긴다 — 시트 위쪽 2행과 오른쪽 5열은 완전히 비어 있다.
//   빈 영역을 그대로 두면 가로 스크롤만 길어지고 도면이 한쪽으로 쏠려 보인다.
const USED = (() => {
  const g = f2FillGrid();
  let r1 = F2_ROWS, r2 = 1, c1 = F2_COLS, c2 = 1;
  const mark = (r, c) => { if (r < r1) r1 = r; if (r > r2) r2 = r; if (c < c1) c1 = c; if (c > c2) c2 = c; };
  g.forEach((row, ri) => row.forEach((f, ci) => { if (f !== F_NONE) mark(ri + 1, ci + 1); }));
  F2_TEXTS.forEach(([r, c]) => mark(r, c));
  return { r1, r2, c1, c2 };
})();
const VB_X = COL_X[USED.c1];
const VB_Y = (USED.r1 - 1) * ROW_H;
const VB_W = COL_X[USED.c2 + 1] - VB_X;
const VB_H = (USED.r2 - USED.r1 + 1) * ROW_H;

// ─── 병합 셀 (화장실 / 3층 계단) ─────────────────────────────
const MERGE_HEAD = new Map();
const MERGE_HIDDEN = new Set();
F2_MERGES.forEach(([r1, c1, r2, c2]) => {
  MERGE_HEAD.set(`${r1},${c1}`, [r2 - r1 + 1, c2 - c1 + 1]);
  for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
    if (r !== r1 || c !== c1) MERGE_HIDDEN.add(`${r},${c}`);
  }
});

// ─── 문 ──────────────────────────────────────────────────────
// v7.344: 사용자가 준 손그림 도면의 흰 사각형 6개 = 문.
//   시트의 벽선이 끊긴 자리(개구부)와 하나씩 정확히 맞아떨어졌다:
//     열33 행19~20  5번방 ↔ 4번방      행21 열36~37  4번방 ↔ 3번방
//     열33 행27~28  2번방 ↔ 3번방      행31 열19~20  2번방 ↔ 1번방
//     열17 행50~51  1번방 좌측         열33 행50~51  1번방 우측
//   ⚠ 열33 행26 에는 [3번방] 라벨, 열17 행52 에는 [1번방] 라벨이 들어 있어
//     개구부 전체가 아니라 빈 칸 구간만 문으로 잡았다.
//   ⚠ 벽선이 그냥 없는 긴 구간(예: 열45 행22~45)은 문이 아니다. 길이로 거르지 말고
//     이 목록으로만 그린다 — 자동 판별하면 그런 구간까지 문이 된다.
//   행53 열18~20 은 이미 [출][입][DOOR] 로 표시된 정문이라 여기서 제외했다.
const DOORS = [
  { dir: 'v', c: 33, a: 19, b: 20 },
  { dir: 'h', r: 21, a: 36, b: 37 },
  { dir: 'v', c: 33, a: 27, b: 28 },
  { dir: 'h', r: 31, a: 19, b: 20 },
  { dir: 'v', c: 17, a: 50, b: 51 },
  { dir: 'v', c: 33, a: 50, b: 51 },
];

// 문이 열리는 방향 — 빈 자리가 더 많은 쪽으로 연다.
//   손그림에는 여닫는 방향이 없어서, 통로가 넓은 쪽으로 여는 것으로 정했다.
function swingSign(grid, hasVal, door) {
  const free = (r, c) =>
    r >= 1 && r <= F2_ROWS && c >= 1 && c <= F2_COLS &&
    grid[r - 1][c - 1] !== F_WALL && !hasVal.has(`${r},${c}`) ? 1 : 0;
  let plus = 0, minus = 0;
  for (let k = door.a; k <= door.b; k++) {
    for (let d = 1; d <= 3; d++) {
      if (door.dir === 'v') { plus += free(k, door.c + d); minus += free(k, door.c - d); }
      else { plus += free(door.r + d, k); minus += free(door.r - d, k); }
    }
  }
  return plus >= minus ? 1 : -1;
}

// ─── 한 글자씩 흩어진 라벨 합치기 ────────────────────────────
// 승/강/기, 회/의/실 처럼 같은 행에서 한 글자 셀이 3칸 이상 이어지면 한 덩이로 본다.
//   ⚠ 2칸까지만 합치면 "출 입" 도 붙어버린다 (출·입은 서로 다른 문이다) → 3칸 이상만.
const isSingleHangul = (v) => typeof v === 'string' && v.length === 1 && /[가-힣]/.test(v);
const buildLabelRuns = () => {
  const byRow = new Map();
  F2_TEXTS.forEach(([r, c, v]) => {
    if (!byRow.has(r)) byRow.set(r, new Map());
    byRow.get(r).set(c, v);
  });
  const runs = [], consumed = new Set();
  byRow.forEach((cols, r) => {
    const sorted = [...cols.keys()].sort((a, b) => a - b);
    let i = 0;
    while (i < sorted.length) {
      if (!isSingleHangul(cols.get(sorted[i]))) { i++; continue; }
      let j = i;
      while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1
             && isSingleHangul(cols.get(sorted[j + 1]))) j++;
      const len = j - i + 1;
      if (len >= 3) {
        const c0 = sorted[i], c1 = sorted[j];
        runs.push({ r, c0, c1, text: sorted.slice(i, j + 1).map(c => cols.get(c)).join('') });
        for (let c = c0; c <= c1; c++) consumed.add(`${r},${c}`);
      }
      i = j + 1;
    }
  });
  return { runs, consumed };
};
const { runs: LABEL_RUNS, consumed: LABEL_CONSUMED } = buildLabelRuns();

// ─── 같은 색 영역을 사각형으로 병합 (탐욕적) ─────────────────
// 칸마다 rect 를 찍으면 2,800개가 되고 경계선이 격자처럼 보인다.
// 가로로 최대한 늘린 뒤, 같은 폭이 이어지는 동안 아래로 늘린다.
function mergeRects(grid, want) {
  const seen = grid.map(r => r.map(() => false));
  const out = [];
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < F2_COLS; c++) {
      if (seen[r][c] || !want(grid[r][c])) continue;
      const f = grid[r][c];
      let c2 = c;
      while (c2 + 1 < F2_COLS && !seen[r][c2 + 1] && grid[r][c2 + 1] === f) c2++;
      let r2 = r;
      for (;;) {
        const nr = r2 + 1;
        if (nr >= grid.length) break;
        let ok = true;
        for (let k = c; k <= c2; k++) if (seen[nr][k] || grid[nr][k] !== f) { ok = false; break; }
        if (!ok) break;
        r2 = nr;
      }
      for (let rr = r; rr <= r2; rr++) for (let cc = c; cc <= c2; cc++) seen[rr][cc] = true;
      out.push({ f, r1: r + 1, c1: c + 1, r2: r2 + 1, c2: c2 + 1 });
    }
  }
  return out;
}
const rectBox = ({ r1, c1, r2, c2 }) => ({
  x: COL_X[c1], y: (r1 - 1) * ROW_H,
  w: COL_X[c2 + 1] - COL_X[c1], h: (r2 - r1 + 1) * ROW_H,
});

// 영역의 바깥 경계선만 뽑는다.
//   ⚠ 병합 사각형마다 테두리를 그리면 영역 안쪽에 이음새가 그어진다.
//     채우기는 테두리 없이 깔고, 이웃이 다른 영역인 변만 따로 긋는다.
function regionOutline(grid, pred) {
  const inR = (r, c) => r >= 1 && r <= F2_ROWS && c >= 1 && c <= F2_COLS && pred(grid[r - 1][c - 1]);
  const d = [];
  for (let r = 1; r <= F2_ROWS; r++) {
    for (let c = 1; c <= F2_COLS; c++) {
      if (!inR(r, c)) continue;
      const x = COL_X[c], y = (r - 1) * ROW_H, w = COL_W(c);
      if (!inR(r - 1, c)) d.push(`M${x} ${y}h${w}`);
      if (!inR(r + 1, c)) d.push(`M${x} ${y + ROW_H}h${w}`);
      if (!inR(r, c - 1)) d.push(`M${x} ${y}v${ROW_H}`);
      if (!inR(r, c + 1)) d.push(`M${x + w} ${y}v${ROW_H}`);
    }
  }
  return d.join('');
}

// 글자 수에 맞춰 폰트 축소 — 4자리 숫자가 칸을 넘지 않게
const fitFont = (text, w, base = 11) => {
  const n = String(text).length;
  const need = n * base * 0.6;
  return need <= w - 5 ? base : Math.max(6.4, (w - 5) / (n * 0.6));
};

/**
 * @param {string[]} hits      하이라이트할 "행,열" 키 배열
 * @param {function} onSelect  칸 클릭 (value, r, c) => void
 * @param {boolean}  dark
 */
export default function NexiFloor2({ hits = [], onSelect, dark = false }) {
  const T = dark ? THEME.dark : THEME.light;
  const hitSet = useMemo(() => new Set(hits), [hits]);

  // ── 배경: 벽 / 선반 / 구역 ── 검색과 무관하므로 테마가 바뀔 때만 다시 만든다
  const background = useMemo(() => {
    const grid = f2FillGrid();
    const els = [];

    // 구역 채우기 (방 · 승강기 · 크림) — 이음새가 안 생기게 테두리 없이 딱 붙여 깐다
    const ZONES = [[F_ROOM, T.room], [F_LIFT, T.lift], [F_CREAM, T.cream]];
    ZONES.forEach(([code, fill]) => {
      mergeRects(grid, f => f === code).forEach((m, i) => {
        const b = rectBox(m);
        els.push(<rect key={`z${code}-${i}`} x={b.x} y={b.y} width={b.w} height={b.h} fill={fill} />);
      });
    });
    // 바깥 경계선만 따로
    [[F_ROOM, T.roomLine], [F_LIFT, T.liftLine], [F_CREAM, T.creamLine]].forEach(([code, stroke], i) => {
      const d = regionOutline(grid, f => f === code);
      if (d) els.push(<path key={`o${i}`} d={d} fill="none" stroke={stroke} strokeWidth={1.3} />);
    });

    // ── 벽 ── v7.345: 칸 단위가 아니라 [연속 구간의 중심선] 으로 그린다.
    //   ⚠ 이전에는 칸마다 바를 놓았다. 가로벽은 칸 폭(37px)을 꽉 채우는데 세로벽은
    //     칸 가운데 8px 이라, 모서리에서 가로벽이 세로벽 중심선을 반 칸(약 18px) 지나쳐
    //     튀어나왔다. 구간 끝을 상황에 따라 정해서 해결한다:
    //       끝 칸에 직교 벽이 붙어 있다(모서리·T자) → 그 칸의 [중심]에서 끝낸다 → 정확히 맞물린다
    //       붙어 있지 않다(자유단·문설주)          → 그 칸의 [바깥 모서리]까지 채운다
    const isWall = (r, c) =>
      r >= 1 && r <= F2_ROWS && c >= 1 && c <= F2_COLS && grid[r - 1][c - 1] === F_WALL;
    const inVRun = (r, c) => isWall(r - 1, c) || isWall(r + 1, c);   // 세로로 이어진 칸인가
    const inHRun = (r, c) => isWall(r, c - 1) || isWall(r, c + 1);   // 가로로 이어진 칸인가

    // 가로 구간
    for (let r = 1; r <= F2_ROWS; r++) {
      let c = 1;
      while (c <= F2_COLS) {
        if (!isWall(r, c)) { c++; continue; }
        const c1 = c;
        while (c <= F2_COLS && isWall(r, c)) c++;
        const c2 = c - 1;
        if (c2 === c1) continue;                       // 홑칸은 세로 구간에서 처리
        const xs = inVRun(r, c1) ? COL_X[c1] + COL_W(c1) / 2 : COL_X[c1];
        const xe = inVRun(r, c2) ? COL_X[c2] + COL_W(c2) / 2 : COL_X[c2 + 1];
        els.push(<rect key={`wh${r}-${c1}`} x={xs} y={(r - 1) * ROW_H + (ROW_H - WALL_T) / 2}
          width={xe - xs} height={WALL_T} fill={T.wall} shapeRendering="crispEdges" />);
      }
    }
    // 세로 구간
    for (let c = 1; c <= F2_COLS; c++) {
      let r = 1;
      while (r <= F2_ROWS) {
        if (!isWall(r, c)) { r++; continue; }
        const r1 = r;
        while (r <= F2_ROWS && isWall(r, c)) r++;
        const r2 = r - 1;
        if (r2 === r1) continue;                       // 홑칸은 가로 구간에서 처리
        const ys = inHRun(r1, c) ? (r1 - 1) * ROW_H + ROW_H / 2 : (r1 - 1) * ROW_H;
        const ye = inHRun(r2, c) ? (r2 - 1) * ROW_H + ROW_H / 2 : r2 * ROW_H;
        els.push(<rect key={`wv${c}-${r1}`} x={COL_X[c] + (COL_W(c) - WALL_T) / 2} y={ys}
          width={WALL_T} height={ye - ys} fill={T.wall} shapeRendering="crispEdges" />);
      }
    }
    // ⚠ 이웃이 하나도 없는 고립된 벽 칸은 그리지 않는다.
    //   도면 전체에서 [행27 열18] 하나뿐이고, 바로 위 [행26 열18] 이 [2번방] 라벨이다.
    //   시트에서 라벨 밑줄로 칠해둔 칸이지 벽이 아니다 — 그리면 허공에 막대가 뜬다.
    // ── 문 ── 건축 도면식 표기: 문짝 선 + 열리는 궤적(호)
    //   ⚠ SVG 는 y 가 아래로 커진다. 호의 sweep 플래그를 화면 기준으로 뒤집어 생각하면
    //     문짝이 반대편으로 붙는다 (아래 주석의 각도는 전부 화면 기준이다).
    const hasVal = new Set(F2_TEXTS.map(([r, c]) => `${r},${c}`));
    DOORS.forEach((door, i) => {
      const sgn = swingSign(grid, hasVal, door);
      let leaf, arc, jamb;
      if (door.dir === 'v') {
        const x = COL_X[door.c] + COL_W(door.c) / 2;
        const y1 = (door.a - 1) * ROW_H, y2 = door.b * ROW_H;
        const wd = y2 - y1;
        // 경첩 (x,y1) → 문짝은 벽에 수직, 호는 문짝 끝에서 반대편 문설주까지
        leaf = `M${x} ${y1}H${x + sgn * wd}`;
        arc = `M${x + sgn * wd} ${y1}A${wd} ${wd} 0 0 ${sgn > 0 ? 1 : 0} ${x} ${y2}`;
        jamb = `M${x - 5} ${y1}h10M${x - 5} ${y2}h10`;
      } else {
        const y = (door.r - 1) * ROW_H + ROW_H / 2;
        const x1 = COL_X[door.a], x2 = COL_X[door.b + 1];
        const wd = x2 - x1;
        leaf = `M${x1} ${y}V${y + sgn * wd}`;
        arc = `M${x1} ${y + sgn * wd}A${wd} ${wd} 0 0 ${sgn > 0 ? 0 : 1} ${x2} ${y}`;
        jamb = `M${x1} ${y - 5}v10M${x2} ${y - 5}v10`;
      }
      els.push(<path key={`dj${i}`} d={jamb} stroke={T.wall} strokeWidth={2.4} fill="none" strokeLinecap="round" />);
      els.push(<path key={`da${i}`} d={arc} stroke={T.doorArc} strokeWidth={1.3} fill="none" strokeDasharray="4 3" />);
      els.push(<path key={`dl${i}`} d={leaf} stroke={T.door} strokeWidth={2.8} fill="none" strokeLinecap="round" />);
    });

    return els;
  }, [T]);

  // ── 값이 있는 칸 ──
  const cells = useMemo(() => {
    const grid = f2FillGrid();
    return F2_TEXTS.map(([r, c, v]) => {
      if (MERGE_HIDDEN.has(`${r},${c}`)) return null;
      if (LABEL_CONSUMED.has(`${r},${c}`)) return null;   // 승강기·회의실은 아래에서 따로
      const [mr, mc] = MERGE_HEAD.get(`${r},${c}`) || [1, 1];
      let w = 0;
      for (let i = 0; i < mc; i++) w += COL_W(c + i);
      return {
        r, c, v, x: COL_X[c], y: (r - 1) * ROW_H, w, h: mr * ROW_H,
        f: grid[r - 1][c - 1], big: mc > 1, code: isF2Code(v),
      };
    }).filter(Boolean);
  }, []);

  return (
    <svg viewBox={`${VB_X} ${VB_Y} ${VB_W} ${VB_H}`} width={VB_W} height={VB_H}
      style={{ display: 'block', fontFamily: 'inherit' }}>
      <style dangerouslySetInnerHTML={{
        __html: `
        .f2-c rect { transition: fill .15s, stroke .15s; }
        .f2-c text { pointer-events: none; user-select: none; }
        .f2-c[data-code="1"] { cursor: pointer; }
        .f2-c[data-code="1"]:hover rect.f2-body { stroke: #2563eb; stroke-width: 1.6; }
        .f2-hit rect.f2-body { fill: #2563eb !important; stroke: #1d4ed8 !important; stroke-width: 1.6 !important; }
        .f2-hit text { fill: #ffffff !important; font-weight: 800 !important; }
        .f2-ring { fill: none; stroke: #2563eb; stroke-width: 2.5; }
        @keyframes f2Pulse {
          0%   { opacity: .95; stroke-width: 2.5; }
          70%  { opacity: 0;   stroke-width: 9; }
          100% { opacity: 0;   stroke-width: 9; }
        }
        .f2-ring-anim { animation-name: f2Pulse; animation-duration: 1.5s; animation-timing-function: ease-out; animation-iteration-count: infinite; }
        @media (prefers-reduced-motion: reduce) {
          /* 애니메이션만 끈다 — 하이라이트 표시는 남는다 (v7.320 교훈) */
          .f2-ring-anim { animation-name: none; opacity: .9; }
        }
      ` }} />

      {background}

      {/* 한 글자씩 흩어져 있던 라벨 (승강기 · 회의실) */}
      {LABEL_RUNS.map(({ r, c0, c1, text }) => {
        const x = COL_X[c0], w = COL_X[c1 + 1] - COL_X[c0], y = (r - 1) * ROW_H;
        return (
          <text key={`lr${r}-${c0}`} x={x + w / 2} y={y + ROW_H / 2}
            textAnchor="middle" dominantBaseline="central"
            fontSize={13} fontWeight={800} fill={T.liftText} letterSpacing="2">{text}</text>
        );
      })}

      {/* 값 칸 */}
      {cells.map(({ r, c, v, x, y, w, h, f, big, code }) => {
        const hit = hitSet.has(`${r},${c}`);
        const isLabel = f === F_LABEL;
        const fill = isLabel ? T.label : big ? T.head : T.cell;
        const line = isLabel ? T.labelLine : big ? T.headLine : T.cellLine;
        // ⚠ 칸 자체는 흰 배경으로 그린다. 구역 색은 뒤에 깔려 있을 뿐이라
        //   글자색을 구역에 맞추면 흰 칸 위에 주황 글씨가 떠서 안 어울린다.
        const color = isLabel ? T.labelText : T.cellText;
        const fs = big ? 14 : fitFont(v, w);
        return (
          <g key={`t${r}-${c}`} className={'f2-c' + (hit ? ' f2-hit' : '')}
            data-code={code ? '1' : '0'}
            onClick={code && onSelect ? () => onSelect(v, r, c) : undefined}>
            <rect className="f2-body" x={x + PAD} y={y + PAD}
              width={w - PAD * 2} height={h - PAD * 2} rx={big ? 6 : 3}
              fill={fill} stroke={line} strokeWidth={big ? 1.2 : 0.9} />
            <text x={x + w / 2} y={y + h / 2} textAnchor="middle" dominantBaseline="central"
              fontSize={fs} fontWeight={isLabel || big ? 800 : 600} fill={color}>{v}</text>
            {hit && (
              <rect className="f2-ring f2-ring-anim" x={x + PAD} y={y + PAD}
                width={w - PAD * 2} height={h - PAD * 2} rx={big ? 6 : 3} />
            )}
          </g>
        );
      })}
    </svg>
  );
}

export { VB_W as F2_PX_W, VB_H as F2_PX_H };
